import {
  Fragment,
  useContext,
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";
import { buildClasses } from "../utils/buildClasses";
import { TransactionColumn } from "./TransactionColumn";
import { SortIcon } from "./TransactionHeaderSortIcon";
import { FilterIcon } from "./TransactionHeaderFilterIcon";
import { TRANSACTION_CELL_PADDING_X } from "./TransactionColumnSizer";
import { useClickOutsideElement } from "../utils/customHooks";
import { TransactionsNavContext } from "./TransactionsScreen";
import { DisableFilteringAndSortingContext } from "./TransactionsTable";
import { keyCodes } from "../utils/KeyCodes";

// Interface that all filter components must implement
export interface TransactionColumnFilterProps {
  isOpen: boolean;
  onClose: () => void;
  hasActiveFilter: boolean;
}

// Helper for constructing the "default" column header component.
export function DefaultTransactionColumnHeader({
  column,
  text,
  align = "LEFT",
  FilterType,
}: {
  column: TransactionColumn;
  text: string;
  align?: "LEFT" | "RIGHT";
  FilterType?: React.ComponentType<TransactionColumnFilterProps> | null;
}): JSX.Element {
  const [transactionsNav] = useContext(TransactionsNavContext);
  const filteringAndSortingDisabled = useContext(
    DisableFilteringAndSortingContext,
  );
  const hasActiveFilter = useMemo(() => {
    return transactionsNav.columnSettings[column].predicate !== null;
  }, [transactionsNav.columnSettings, column]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterComponentRef = useRef<HTMLDivElement>(null);
  useClickOutsideElement(
    filterComponentRef,
    () => setIsFilterOpen(false),
    isFilterOpen,
    window,
  );

  function toggleFilter() {
    setIsFilterOpen(!isFilterOpen);
  }
  function handleFilterKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.repeat) {
      return;
    }
    if (e.code === keyCodes.ESC) {
      // If a child input is focused, let it handle Escape first
      // (e.g., month picker closes its dropdown, numerical input handles its own onEsc)
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
      }
      setIsFilterOpen(false);
    }
  }

  // Close the filter on Escape when no child input is focused
  // (e.g., month picker dropdown is closed and input is blurred)
  useEffect(() => {
    if (!isFilterOpen) return;
    function handleDocumentKeyDown(e: KeyboardEvent) {
      if (e.repeat || e.code !== keyCodes.ESC) return;
      const container = filterComponentRef.current;
      if (!container) return;
      const activeEl = document.activeElement;
      // If no input/textarea inside our filter is focused, close the filter
      const focusedInput =
        activeEl &&
        container.contains(activeEl) &&
        (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA");
      if (!focusedInput) {
        setIsFilterOpen(false);
      }
    }
    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => document.removeEventListener("keydown", handleDocumentKeyDown);
  }, [isFilterOpen]);
  function handleFilterBlur(e: React.FocusEvent<HTMLDivElement>) {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      // Don't close if focus moved to a FloatingPortal (nested dropdown)
      const appRoot = document.getElementById("root");
      if (
        e.relatedTarget &&
        appRoot &&
        !appRoot.contains(e.relatedTarget as Node)
      ) {
        return;
      }
      setIsFilterOpen(false);
    }
  }

  const filterIcon = FilterType && !filteringAndSortingDisabled && (
    <div
      ref={filterComponentRef}
      className={buildClasses("flex", "items-center")}
    >
      {/* preventDefault on mousedown keeps focus on any currently-focused
          input inside the filter body. Without this, clicking the icon while
          the filter is open would blur the input, trigger handleFilterBlur to
          close the filter, then the click would toggle it back open. */}
      <div
        className={buildClasses("flex", "items-center")}
        onMouseDown={(e) => e.preventDefault()}
      >
        <FilterIcon hasActiveFilter={hasActiveFilter} onClick={toggleFilter} />
      </div>
      <div
        className={buildClasses(
          "absolute",
          "top-full",
          {
            if: align === "RIGHT",
            then: "right-0",
            else: "left-0",
          },
          "z-50",
          "mt-1",
          {
            if: isFilterOpen,
            then: "visible",
            else: "invisible",
          },
        )}
        onBlur={handleFilterBlur}
        onKeyDown={handleFilterKeyDown}
        tabIndex={-1}
      >
        <FilterType
          isOpen={isFilterOpen}
          onClose={toggleFilter}
          hasActiveFilter={hasActiveFilter}
        />
      </div>
    </div>
  );

  return (
    <div className={buildClasses("relative", "w-full")}>
      <div
        className={buildClasses(
          {
            if: align === "RIGHT",
            then: "justify-end",
            else: "justify-start",
          },
          "w-full",
          "flex",
          "items-center",
          TRANSACTION_CELL_PADDING_X,
          "group", // https://tailwindcss.com/docs/hover-focus-and-other-states#styling-based-on-parent-state
        )}
      >
        {align === "RIGHT" ? (
          <Fragment>
            {filterIcon}
            <div className={buildClasses("w-1")} />
            <SortIcon column={column} />
            <div className={buildClasses("w-3")} />
          </Fragment>
        ) : null}
        <div className={buildClasses("min-w-0", "truncate")}>{text}</div>
        {align === "LEFT" ? (
          <Fragment>
            <div className={buildClasses("w-3")} />
            <SortIcon column={column} />
            <div className={buildClasses("w-1")} />
            {filterIcon}
          </Fragment>
        ) : null}
      </div>
    </div>
  );
}
